import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Header from '@/components/Header';
import { HeartStraight, Check, CalendarCheck } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getSubscribeButtonLabel = (subscription, subscribing) => {
  if (subscription) return 'Already Subscribed';
  if (subscribing) return 'Processing...';
  return 'Subscribe';
};

const FitnessPage = ({ user }) => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const plansRes = await axios.get(`${BACKEND_URL}/api/subscriptions/plans`);
      setPlans(plansRes.data);
      try {
        const subRes = await axios.get(`${BACKEND_URL}/api/subscriptions/my-subscription`, { withCredentials: true });
        setSubscription(subRes.data);
      } catch (subErr) {
        // Subscription 401 is expected for logged-out users — silent
      }
    } catch (error) {
      toast.error('Could not load plans. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubscribe = async (planId) => {
    setSubscribing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/subscriptions/subscribe`,
        { plan_id: planId },
        { withCredentials: true }
      );
      toast.success('Subscription activated successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handlePause = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/subscriptions/pause`, {}, { withCredentials: true });
      toast.success('Subscription paused');
      fetchData();
    } catch (error) {
      toast.error('Failed to pause subscription');
    }
  };

  const handleResume = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/subscriptions/resume`, {}, { withCredentials: true });
      toast.success('Subscription resumed');
      fetchData();
    } catch (error) {
      toast.error('Failed to resume subscription');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <Header user={user} />
        <div className="flex items-center justify-center py-24">
          <div className="w-16 h-16 border-4 border-[#1A4331] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Header user={user} />

      <main className="px-6 md:px-12 lg:px-24 py-12" data-testid="fitness-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <HeartStraight size={40} weight="duotone" className="text-[#C05C3D]" />
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading" data-testid="fitness-heading">Fitness Meal Plans</h1>
          </div>
          <p className="text-base leading-relaxed text-stone-600 max-w-2xl">Subscribe to personalized meal plans designed for your fitness goals. All meals include complete nutrition information.</p>
        </motion.div>

        {subscription && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 md:p-8 mb-12" data-testid="active-subscription">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <CalendarCheck size={32} weight="duotone" className="text-[#34D399]" />
                <div>
                  <h3 className="text-xl font-medium text-[#1C1917] font-heading">{subscription.plan_details?.name}</h3>
                  <p className="text-stone-600">Status: <span className={`font-medium capitalize ${subscription.status === 'active' ? 'text-[#34D399]' : 'text-[#FBBF24]'}`}>{subscription.status}</span></p>
                </div>
              </div>
              {subscription.status === 'active' ? (
                <button onClick={handlePause} className="border border-stone-300 text-stone-900 hover:bg-stone-100 px-4 py-2 rounded-full font-medium transition-all duration-200" data-testid="pause-subscription-btn">Pause</button>
              ) : (
                <button onClick={handleResume} className="bg-[#34D399] text-stone-900 hover:bg-[#10B981] px-4 py-2 rounded-full font-medium transition-all duration-200" data-testid="resume-subscription-btn">Resume</button>
              )}
            </div>
            <p className="text-sm text-stone-600">Valid until {new Date(subscription.end_date).toLocaleDateString()}</p>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <motion.div key={plan.plan_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * index }} className="bg-white border border-stone-200 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 rounded-2xl p-6" data-testid={`plan-card-${plan.type}`}>
              <h3 className="text-xl font-medium text-[#1C1917] mb-2 font-heading">{plan.name}</h3>
              <div className="mb-4">
                <span className="text-3xl font-bold text-[#1A4331]">₹{plan.price.toLocaleString()}</span>
                <span className="text-stone-600 text-sm">/{plan.type}</span>
              </div>
              <p className="text-sm text-stone-600 mb-4">{plan.meals_per_day} meals per day for {plan.duration_months} months</p>
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-stone-600">
                    <Check size={16} weight="bold" className="text-[#34D399] mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => handleSubscribe(plan.plan_id)} disabled={subscribing || subscription} className="w-full bg-[#C05C3D] text-white hover:bg-[#D96A47] px-6 py-3 rounded-full font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`subscribe-btn-${plan.type}`}>
                {getSubscribeButtonLabel(subscription, subscribing)}
              </button>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default FitnessPage;